// TES-1330 (TES-1327 §7) — "Rekommenderade kompletteringar att ta ställning
// till" above the meeting note. Drive A-02, verbatim:
//
//   "Rekommendationerna härleds från beslutade kliniska regler och det
//    underlag som faktiskt finns, exempelvis nytt blodprov, blodtryck och
//    vilopuls eller midjemått enligt beslutad BMI-regel. Läkaren ska kunna
//    använda rekommendationen som stöd under samtalet och därefter själv
//    bekräfta vilka kompletteringar som ska begäras. Inget skickas till
//    patienten utan läkarens godkännande."
//
// Only rules that are already decided produce a recommendation:
//
//   1. Blood pressure and resting pulse. A-02: "Blodtryck/vilopuls är känt
//      obligatoriskt basunderlag när giltiga värden saknas; övriga val avgör
//      Kirill." The same rule already drives the picker's hint (Ö-244 (b),
//      completion-picker.ts). "Giltiga värden saknas" is read as: no reading,
//      a reading with one half missing, or a reading the doctor himself
//      assessed as "Behöver ny mätning" (TES-1056). No age limit: Emelie
//      declined one (Ö-235, 2026-09-24).
//   2. Waist per the BMI rule. A-02 "ÖVERORDNAT KANONISKT BESLUT — BMI
//      25,0–26,9": "Läkaradmin beräknar midja/längd-kvot, WHtR = midjemått i
//      cm / längd i cm". When F-02's low-BMI section (TES-1116) has no waist,
//      WHtR cannot be computed.
//
// Deliberately absent: a new blood test. No decided rule says when one is
// needed — the age of an earlier result has no threshold (Ö-235: "ska inte
// fatta det kliniska beslutet åt läkaren") — so it is the doctor's own call
// until a rule is decided (asked as Ö-546 in TES-475).
//
// A recommendation never ticks anything (Ö-244 (b): "Systemet får inte
// automatiskt lägga till ett fast kompletteringspaket.") and never creates a
// patient task: it names which option of the completion picker it concerns,
// and the picker shows that beside the option.
//
// Pure on purpose: no React, no path aliases, so `node --test` runs it.
import type {
  ClinicianMedicalEvidence,
  CompletionItem,
  EvidenceSummary,
  LowBmiAssessment,
} from "@care-by-ella/contracts";

export type RecommendationKey = "blood_pressure_pulse" | "waist";

export interface RecommendedCompletion {
  key: RecommendationKey;
  label: string;
  /** Why, from the evidence actually on file. */
  reason: string;
  /** The picker option the doctor would tick to request it. */
  pickerItem: CompletionItem;
}

export interface RecommendationInput {
  evidence: Pick<EvidenceSummary, "bloodPressurePulse"> | null;
  lowBmi: Pick<LowBmiAssessment, "section"> | null;
  medicalEvidence: Pick<ClinicianMedicalEvidence, "active" | "tasks"> | null;
}

function bloodPressurePulseReason(
  bp: EvidenceSummary["bloodPressurePulse"],
): string | null {
  // The picker's own hint, Emelie's words (Ö-244 (b)).
  if (!bp)
    return "Blodtryck och vilopuls saknas – behövs normalt som basunderlag.";
  if (bp.source !== "clinician" && bp.reviewStatus === "needs_new_measurement")
    return "Senaste mätningen är bedömd som ”Behöver ny mätning”.";
  if (bp.systolic === null)
    return "Blodtryck saknas i senaste mätningen – behövs som basunderlag.";
  if (bp.pulse === null)
    return "Vilopuls saknas i senaste mätningen – behövs som basunderlag.";
  return null;
}

export function recommendedCompletions(
  input: RecommendationInput,
): RecommendedCompletion[] {
  const out: RecommendedCompletion[] = [];

  // Already requested and still open: asking again adds nothing.
  const openRequest = (key: string) =>
    !!input.medicalEvidence?.active &&
    input.medicalEvidence.tasks.some(
      (t) => t.key === key && t.status === "missing",
    );

  // Unknown (a failed read) is not "missing": no recommendation from it.
  if (input.evidence && !openRequest("blood_pressure_pulse")) {
    const reason = bloodPressurePulseReason(input.evidence.bloodPressurePulse);
    if (reason) {
      out.push({
        key: "blood_pressure_pulse",
        label: "Blodtryck och vilopuls",
        reason,
        pickerItem: "blood_pressure_pulse",
      });
    }
  }

  // Only a patient whose F-02 showed the BMI 25,0–26,9 section: for every
  // other patient the rule does not apply ("midjemått när den frågan har
  // visats").
  const measures = input.lowBmi?.section?.measures;
  if (measures && measures.waistCm === null) {
    out.push({
      key: "waist",
      label: "Midjemått",
      reason: "BMI 25,0–26,9: midjemått saknas, så WHtR kan inte beräknas.",
      pickerItem: "other",
    });
  }

  return out;
}

/** What the picker shows beside an option a recommendation points at. */
export function pickerRecommendationNotes(
  recs: readonly RecommendedCompletion[],
): Partial<Record<CompletionItem, string>> {
  const labels: Partial<Record<CompletionItem, string[]>> = {};
  for (const r of recs) {
    (labels[r.pickerItem] ??= []).push(r.label.toLowerCase());
  }
  const notes: Partial<Record<CompletionItem, string>> = {};
  for (const [item, ls] of Object.entries(labels) as [
    CompletionItem,
    string[],
  ][]) {
    notes[item] = `Rekommenderat ovan: ${ls.join(", ")}`;
  }
  return notes;
}
